import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isApiError } from '@/apis/apiErrors';
import type { SeriesBySensor, TelemetryVital } from '@/apis/telemetry';
import { ChunkName, seriesChunks, type SeriesChunk } from './lib/chunks';
import { devicesOf, type Device } from './lib/devices';
import type { Extent } from './lib/extent';
import {
  appliedCharts,
  availableCharts,
  CHART_ORDER,
  shownCharts,
} from './lib/measures';
import {
  DataRange,
  notLoadedSpans,
  rangeView,
  type RangeView,
} from './lib/range';
import {
  ChunkStatus,
  useSensorModalPreferences,
  useTelemetrySeries,
  useTelemetrySummary,
} from './queries';

/** What the modal body shows: a spinner, an error, "no permission", "no sensor data", "no trip window", or the charts. */
export enum TimelineStatus {
  Loading = 'loading',
  Failed = 'failed',
  Forbidden = 'forbidden',
  NoData = 'no-data',
  Empty = 'empty',
  Ready = 'ready',
}

/** Everything the Sensor modal renders, and the actions its controls call. */
export interface SensorTimelineState {
  status: TimelineStatus;
  available: TelemetryVital[];
  charts: TelemetryVital[];
  chartStatus: Partial<Record<TelemetryVital, ChunkStatus>>;
  devices: Device[];
  series: SeriesBySensor;
  window: Extent | null;
  range: DataRange;
  view: RangeView | null;
  notLoaded: Partial<Record<TelemetryVital, Extent[]>>;
  hidden: ReadonlySet<string>;
  toggleDevice: (deviceId: string) => void;
  applyCharts: (picked: readonly TelemetryVital[]) => void;
  setRange: (range: DataRange) => void;
  showEvents: boolean;
  setShowEvents: (on: boolean) => void;
  focusChart: TelemetryVital | null;
  chartFocused: () => void;
}

/**
 * Loads a shipment's summary, saved chart choice and operational-window readings for the Sensor
 * modal. Every vital a device reported, at any time, is offered (PRD §4.1); one with no readings in the
 * trip opens as an empty chart and shows in the full range. An in-transit window ends when the summary
 * loaded. The range starts on the operational window each time the modal opens. Opened on a `focus` vital the
 * shipment has, it adds that chart to the saved ones once when it isn't there, then names it as `focusChart` until
 * the stack reports it with `chartFocused`.
 */
export const useSensorTimeline = (
  shipmentId: string,
  shareToken?: string,
  focus?: TelemetryVital
): SensorTimelineState => {
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());
  const [focusing, setFocusing] = useState(focus ?? null);
  const focusAdded = useRef(false);
  const [range, setRange] = useState(DataRange.Operational);
  const summaryQuery = useTelemetrySummary(shipmentId, {
    enabled: true,
    shareToken,
  });
  const summary = summaryQuery.data;
  const now = summaryQuery.dataUpdatedAt;
  const { preferences, loaded, update } = useSensorModalPreferences({
    anonymous: Boolean(shareToken),
  });

  const { chunks, available, devices, window } = useMemo(() => {
    const windows: SeriesChunk[] = summary
      ? seriesChunks(summary.anchors, now)
      : [];
    const operational = windows.find(
      (chunk) => chunk.name === ChunkName.Operational
    );
    const operationalWindow: Extent | null = operational
      ? [operational.from, operational.to]
      : null;
    return {
      chunks: windows,
      available: availableCharts(summary),
      devices: devicesOf(summary?.sensors ?? []),
      window: operationalWindow,
    };
  }, [summary, now]);

  const charts = useMemo(
    () => shownCharts(preferences.charts, available),
    [preferences.charts, available]
  );

  useEffect(() => {
    if (!focusing || !window || !loaded) return;
    if (!available.includes(focusing)) {
      setFocusing(null);
    } else if (!focusAdded.current && !preferences.charts.includes(focusing)) {
      focusAdded.current = true;
      update({
        charts: CHART_ORDER.filter(
          (key) => key === focusing || preferences.charts.includes(key)
        ),
      });
    }
  }, [focusing, window, loaded, available, preferences.charts, update]);

  const { series, operational, byChunk } = useTelemetrySeries(
    shipmentId,
    summary,
    charts,
    { now, shareToken }
  );

  const view = useMemo(
    () => (summary ? rangeView(range, chunks, summary.anchors) : null),
    [range, chunks, summary]
  );
  const notLoaded = useMemo(
    () =>
      view?.marked
        ? Object.fromEntries(
            charts.map((key) => [key, notLoadedSpans(chunks, byChunk, key)])
          )
        : {},
    [view, charts, chunks, byChunk]
  );

  const toggleDevice = useCallback((deviceId: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (!next.delete(deviceId)) next.add(deviceId);
      return next;
    });
  }, []);

  const applyCharts = useCallback(
    (picked: readonly TelemetryVital[]) =>
      update({ charts: appliedCharts(picked, preferences.charts, available) }),
    [update, preferences.charts, available]
  );

  const setShowEvents = useCallback(
    (on: boolean) => update({ show_events: on }),
    [update]
  );

  const chartFocused = useCallback(() => setFocusing(null), []);

  return {
    status: statusOf(summaryQuery, available.length > 0, window),
    available,
    charts,
    chartStatus: operational,
    devices,
    series,
    window,
    range,
    view,
    notLoaded,
    hidden,
    toggleDevice,
    applyCharts,
    setRange,
    showEvents: preferences.show_events,
    setShowEvents,
    focusChart:
      loaded && focusing && charts.includes(focusing) ? focusing : null,
    chartFocused,
  };
};

const statusOf = (
  summary: { status: 'pending' | 'error' | 'success'; error: unknown },
  hasSensorData: boolean,
  window: Extent | null
): TimelineStatus => {
  if (summary.status === 'pending') return TimelineStatus.Loading;
  if (summary.status === 'error') {
    return isApiError(summary.error) && summary.error.response?.status === 403
      ? TimelineStatus.Forbidden
      : TimelineStatus.Failed;
  }
  if (!hasSensorData) return TimelineStatus.NoData;
  return window ? TimelineStatus.Ready : TimelineStatus.Empty;
};
